import type { AdminRunDetailResponse, AdminRunSegmentKey, AdminRunTraceSpan } from '@/api/adminClient';
import { topLevelShare } from '@/lib/runTrace';
import { formatDurationMs, runStatusLabel, type StatusTone } from '@/lib/runTelemetry';

export type VerdictBucketKey = 'startup' | 'workspace' | 'context' | 'model' | 'reading' | 'failure' | 'rest';

type BucketMeta = { label: string; phrase: string; swatch: string };

export const VERDICT_BUCKETS: Record<VerdictBucketKey, BucketMeta> = {
  startup: { label: 'Getting started', phrase: 'getting the run started', swatch: 'bg-chart-3' },
  workspace: { label: 'Waiting for a workspace', phrase: 'waiting for a workspace', swatch: 'bg-chart-4' },
  context: { label: 'Preparing the request', phrase: "preparing the agent's request", swatch: 'bg-chart-2' },
  model: { label: 'Waiting for the model', phrase: 'waiting for the model to start answering', swatch: 'bg-warning' },
  reading: { label: 'Reading code before the first edit', phrase: 'the agent reading code before its first edit', swatch: 'bg-brand' },
  failure: { label: 'Until it failed', phrase: 'spent before it failed', swatch: 'bg-destructive' },
  rest: { label: 'Writing the app and building the preview', phrase: 'writing the app and building the preview', swatch: 'bg-chart-1' },
};

const SEGMENT_BUCKET: Record<AdminRunSegmentKey, VerdictBucketKey> = {
  handoff: 'startup',
  agent_boot: 'startup',
  handoff_and_seat: 'workspace',
  seat_queue: 'workspace',
  seat_claim: 'workspace',
  context_prep: 'context',
  model_ttft: 'model',
  first_token_to_tool: 'model',
  model_and_agent: 'model',
  explore_before_edit: 'reading',
  until_failure: 'failure',
};

const TRACE_BUCKET: Record<string, VerdictBucketKey> = {
  queue: 'startup',
  claim: 'workspace',
  'agent-boot': 'startup',
  agent: 'rest',
  delivery: 'rest',
};

const FAILED_STAGE: Record<string, string> = {
  queue: 'start',
  claim: 'workspace',
  'agent-boot': 'agent start',
  agent: 'agent turn',
  delivery: 'publish',
  'save-source': 'save',
  dependencies: 'install',
  'preview-build': 'preview build',
  'preview-upload': 'publish',
  'backend-deploy': 'backend deploy',
  'app-service-deploy': 'App Service deploy',
};

const TERMINAL_STATUSES = new Set(['preview_ready', 'failed', 'blocked']);

/** A bucket shorter than this share of the run is not worth naming in the sentence. */
const MEANINGFUL_SHARE = 0.4;

export type VerdictBucket = { key: VerdictBucketKey; label: string; swatch: string; durationMs: number; share: number };

export type RunVerdict = {
  status: { label: string; tone: StatusTone };
  model?: string;
  startedAt?: string;
  totalMs?: number;
  sentence: string;
  buckets: VerdictBucket[];
  error?: string;
  failed: boolean;
};

function failedStage(spans: AdminRunTraceSpan[]): string | undefined {
  const errored = spans.filter((span) => span.status === 'error' && span.id !== 'failed');
  if (errored.length === 0) return undefined;
  const deepest = [...errored].sort((a, b) => Number(Boolean(b.parentId)) - Number(Boolean(a.parentId)) || b.startMs - a.startMs)[0];
  return FAILED_STAGE[deepest.id] ?? FAILED_STAGE[deepest.parentId ?? ''] ?? FAILED_STAGE[deepest.id.split('.')[0]];
}

function totalDurationMs(detail: AdminRunDetailResponse, nowMs: number): number | undefined {
  const { run, trace } = detail;
  if (trace) {
    if (trace.endMs !== undefined && !trace.inProgress) return trace.endMs;
    const origin = Date.parse(trace.origin.at);
    if (trace.inProgress && Number.isFinite(origin)) return Math.max(0, nowMs - origin);
  }
  const started = run.createdAt ? Date.parse(run.createdAt) : NaN;
  if (!Number.isFinite(started)) return undefined;
  if (!TERMINAL_STATUSES.has(run.status)) return Math.max(0, nowMs - started);
  const ended = run.updatedAt ? Date.parse(run.updatedAt) : NaN;
  return Number.isFinite(ended) && ended >= started ? ended - started : undefined;
}

function collectBuckets(detail: AdminRunDetailResponse, totalMs: number | undefined, nowMs: number): VerdictBucket[] {
  const sums = new Map<VerdictBucketKey, number>();
  const add = (key: VerdictBucketKey, ms: number) => {
    if (ms > 0) sums.set(key, (sums.get(key) ?? 0) + ms);
  };
  const breakdown = detail.telemetry.waitBreakdown;
  if (breakdown && breakdown.totalMs > 0) {
    for (const segment of breakdown.segments) add(SEGMENT_BUCKET[segment.key], segment.durationMs);
    if (totalMs !== undefined && breakdown.toKey !== 'run.failed') add('rest', totalMs - breakdown.totalMs);
  } else if (detail.trace) {
    for (const span of topLevelShare(detail.trace, Math.max(0, nowMs - Date.parse(detail.trace.origin.at)))) {
      add(TRACE_BUCKET[span.id] ?? 'rest', span.durationMs);
    }
  }
  const sum = [...sums.values()].reduce((total, ms) => total + ms, 0);
  const scale = Math.max(sum, totalMs ?? 0);
  if (scale <= 0) return [];
  return (Object.keys(VERDICT_BUCKETS) as VerdictBucketKey[])
    .filter((key) => sums.has(key))
    .map((key) => {
      const durationMs = sums.get(key) ?? 0;
      return { key, label: VERDICT_BUCKETS[key].label, swatch: VERDICT_BUCKETS[key].swatch, durationMs, share: durationMs / scale };
    });
}

function runStatus(detail: AdminRunDetailResponse): { label: string; tone: StatusTone; failed: boolean } {
  const { run, trace, events } = detail;
  switch (run.status) {
    case 'preview_ready':
      return { label: 'Succeeded', tone: 'success', failed: false };
    case 'failed': {
      if (events.some((event) => event.key === 'run.cancelled' || event.type === 'run.cancelled')) {
        return { label: 'Stopped', tone: 'neutral', failed: false };
      }
      const stage = (trace && failedStage(trace.spans)) ?? (run.backendError ? 'backend deploy' : run.appServiceError ? 'App Service deploy' : undefined);
      return { label: stage ? `Failed at ${stage}` : 'Failed', tone: 'danger', failed: true };
    }
    case 'blocked':
      return { label: 'Blocked', tone: 'danger', failed: true };
    case 'queued':
      return { label: 'Waiting to start', tone: 'warning', failed: false };
    case 'running':
    case 'validating':
    case 'snapshotting':
      return { label: 'Still running', tone: 'active', failed: false };
    default:
      return { label: runStatusLabel(run.status), tone: 'neutral', failed: false };
  }
}

function firstError(detail: AdminRunDetailResponse): string | undefined {
  const { run, trace, events } = detail;
  const fromTrace = trace?.spans.find((span) => span.id === 'failed')?.attrs?.error;
  const fromEvent = events.find((event) => event.key === 'run.failed')?.payload;
  const candidates = [run.error, run.backendError, run.appServiceError, fromTrace, fromEvent?.error, fromEvent?.message];
  return candidates.find((value): value is string => typeof value === 'string' && value.trim() !== '')?.trim();
}

function modelSummary(detail: AdminRunDetailResponse): string | undefined {
  const { run, telemetry } = detail;
  const model = telemetry.model.requested ?? run.executorModel ?? telemetry.model.resolved[0];
  const effort = telemetry.effort.effective ?? telemetry.effort.requested ?? run.executorEffort;
  if (!model) return effort ? `${effort} effort` : undefined;
  return effort ? `${model} · ${effort} effort` : model;
}

/** What an admin needs first about a run: how it ended, how long it took, where the time went and the error. */
export function runVerdict(detail: AdminRunDetailResponse, nowMs: number = Date.now()): RunVerdict {
  const status = runStatus(detail);
  const totalMs = totalDurationMs(detail, nowMs);
  const buckets = collectBuckets(detail, totalMs, nowMs);
  const running = !TERMINAL_STATUSES.has(detail.run.status);

  let sentence: string;
  if (totalMs === undefined) {
    sentence = running ? 'This run has not recorded any timing yet.' : 'No timing was recorded for this run.';
  } else {
    sentence = running ? `Running for ${formatDurationMs(totalMs)} so far.` : `Took ${formatDurationMs(totalMs)}.`;
    const largest = buckets.reduce<VerdictBucket | undefined>((best, bucket) => (!best || bucket.durationMs > best.durationMs ? bucket : best), undefined);
    if (largest && largest.share >= MEANINGFUL_SHARE && buckets.length > 1) {
      sentence += ` ${formatDurationMs(largest.durationMs)} of that was ${VERDICT_BUCKETS[largest.key].phrase}.`;
    }
  }

  return {
    status: { label: status.label, tone: status.tone },
    model: modelSummary(detail),
    startedAt: detail.run.createdAt,
    totalMs,
    sentence,
    buckets,
    error: status.failed ? firstError(detail) : undefined,
    failed: status.failed,
  };
}
